import { apiGet, apiPut, apiPost, apiPatch, apiDelete } from "./client";
import type {
  CandidateGraphSnapshot,
  CandidateGraphPutBody,
  CandidateGraphDraft,
  CandidateGraphMeta,
  JobExperience,
  SkillNode,
  EducationEntry,
  LicenseEntry,
  CertificationEntry,
  NarrativeLens,
  NarrativeLensId,
  AtsPastePack,
  CandidateJobRecord,
  CandidateSkillRecord,
  CandidateEducationRecord,
  CandidateLicenseRecord,
  CandidateCertificationRecord,
  CandidateLensRecord,
} from "@career-ops-amplify/shared";

export async function getCandidateGraph(): Promise<CandidateGraphSnapshot> {
  return apiGet<CandidateGraphSnapshot>("/api/candidate-graph");
}

export async function putCandidateGraph(
  body: CandidateGraphPutBody,
): Promise<CandidateGraphSnapshot> {
  return apiPut<CandidateGraphSnapshot>("/api/candidate-graph", body);
}

export async function patchCandidateGraphMeta(
  patch: Partial<
    Pick<
      CandidateGraphMeta,
      | "defaultLens"
      | "identity"
      | "preferences"
      | "workAuthorization"
      | "markdownManualOverride"
    >
  >,
): Promise<{ meta: CandidateGraphMeta }> {
  return apiPatch<{ meta: CandidateGraphMeta }>(
    "/api/candidate-graph/meta",
    patch,
  );
}

export async function createCandidateJob(
  job: JobExperience,
): Promise<{ job: CandidateJobRecord }> {
  return apiPost("/api/candidate-graph/jobs", job);
}

export async function updateCandidateJob(
  id: string,
  job: JobExperience,
): Promise<{ job: CandidateJobRecord }> {
  return apiPut(`/api/candidate-graph/jobs/${id}`, job);
}

export async function deleteCandidateJob(id: string): Promise<void> {
  await apiDelete(`/api/candidate-graph/jobs/${id}`);
}

export async function createCandidateSkill(
  skill: SkillNode,
): Promise<{ skill: CandidateSkillRecord }> {
  return apiPost("/api/candidate-graph/skills", skill);
}

export async function updateCandidateSkill(
  id: string,
  skill: SkillNode,
): Promise<{ skill: CandidateSkillRecord }> {
  return apiPut(`/api/candidate-graph/skills/${id}`, skill);
}

export async function deleteCandidateSkill(id: string): Promise<void> {
  await apiDelete(`/api/candidate-graph/skills/${id}`);
}

export async function createCandidateEducation(
  edu: EducationEntry,
): Promise<{ education: CandidateEducationRecord }> {
  return apiPost("/api/candidate-graph/education", edu);
}

export async function updateCandidateEducation(
  id: string,
  edu: EducationEntry,
): Promise<{ education: CandidateEducationRecord }> {
  return apiPut(`/api/candidate-graph/education/${id}`, edu);
}

export async function deleteCandidateEducation(id: string): Promise<void> {
  await apiDelete(`/api/candidate-graph/education/${id}`);
}

export async function createCandidateLicense(
  lic: LicenseEntry,
): Promise<{ license: CandidateLicenseRecord }> {
  return apiPost("/api/candidate-graph/licenses", lic);
}

export async function deleteCandidateLicense(id: string): Promise<void> {
  await apiDelete(`/api/candidate-graph/licenses/${id}`);
}

export async function createCandidateCertification(
  cert: CertificationEntry,
): Promise<{ certification: CandidateCertificationRecord }> {
  return apiPost("/api/candidate-graph/certifications", cert);
}

export async function deleteCandidateCertification(id: string): Promise<void> {
  await apiDelete(`/api/candidate-graph/certifications/${id}`);
}

export async function putCandidateLens(
  id: string,
  lens: NarrativeLens,
): Promise<{ lens: CandidateLensRecord }> {
  return apiPut(`/api/candidate-graph/lenses/${encodeURIComponent(id)}`, lens);
}

export async function deleteCandidateLens(id: string): Promise<void> {
  await apiDelete(`/api/candidate-graph/lenses/${encodeURIComponent(id)}`);
}

export async function assembleCandidateMarkdown(opts?: {
  force?: boolean;
  lensId?: NarrativeLensId;
}): Promise<{ content: string; skipped: boolean }> {
  return apiPost("/api/candidate-graph/assemble", opts ?? {});
}

export async function parseCandidateMarkdown(markdown?: string): Promise<{
  draft: CandidateGraphDraft;
}> {
  return apiPost("/api/candidate-graph/parse", { markdown });
}

export async function commitCandidateDraft(
  draft: CandidateGraphDraft,
): Promise<CandidateGraphSnapshot> {
  return apiPost("/api/candidate-graph/commit-draft", {
    draft,
    confirmed: true,
  });
}

export async function getAtsPastePack(
  lens?: NarrativeLensId,
): Promise<AtsPastePack> {
  const q = lens ? `?lens=${encodeURIComponent(lens)}` : "";
  return apiGet(`/api/candidate-graph/paste-pack${q}`);
}
